import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  listAccessKeys,
  createAccessKey,
  renameAccessKey,
  deleteAccessKey,
  deleteSessions,
  deleteAllSessions,
  type AccessKey,
} from "../api/access-keys.api";
import { ApiError } from "@/lib/api/client";

/** Query key factory for CLI access keys */
export const accessKeysKeys = {
  all: ["access-keys"] as const,
  list: () => [...accessKeysKeys.all, "list"] as const,
};

export function useAccessKeys() {
  return useQuery<{ accessKeys: AccessKey[] }, ApiError>({
    queryKey: accessKeysKeys.list(),
    queryFn: listAccessKeys,
  });
}

export function useCreateAccessKey() {
  const queryClient = useQueryClient();

  return useMutation<{ accessKey: AccessKey }, ApiError, { friendlyName: string; ttl?: number }>({
    mutationFn: createAccessKey,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: accessKeysKeys.all });
    },
  });
}

export function useRenameAccessKey() {
  const queryClient = useQueryClient();

  return useMutation<
    { accessKey: AccessKey },
    ApiError,
    { friendlyName: string; newFriendlyName: string }
  >({
    mutationFn: ({ friendlyName, newFriendlyName }) =>
      renameAccessKey(friendlyName, newFriendlyName),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: accessKeysKeys.all });
    },
  });
}

export function useDeleteAccessKey() {
  const queryClient = useQueryClient();

  return useMutation<void, ApiError, string>({
    mutationFn: deleteAccessKey,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: accessKeysKeys.all });
    },
  });
}

export function useDeleteSessions() {
  const queryClient = useQueryClient();

  return useMutation<void, ApiError, string>({
    mutationFn: deleteSessions,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: accessKeysKeys.all });
    },
  });
}

export function useDeleteAllSessions(options: { onSuccess?: () => void } = {}) {
  // Hook-level onSuccess so the sign-out still runs if the caller unmounts before the answer.
  // No invalidation: the caller signs out on success and the whole cache goes with it.
  return useMutation<void, ApiError, void>({
    mutationFn: deleteAllSessions,
    onSuccess: options.onSuccess,
  });
}
